/**
 * Share-mode context. ShareBanner is the single fetch point — it resolves
 * the ?share=<token> query param against /api/share/{token} and pushes
 * the metadata here so deeper components (DocsLayout's sidebar, etc.)
 * can adjust their behavior without re-fetching.
 *
 * When the user is logged in normally (no share token in URL), this
 * context just reports `share = null` and components render as usual.
 */
import { createContext, useContext } from "react";

export interface ShareRule { slug: string; includeChildren: boolean }

export interface ShareInfo {
  projectSlug: string;
  branchId: string;
  pinnedCommit: string | null;
  rules: ShareRule[];
  expiresAt: string | null;
}

const ShareContext = createContext<ShareInfo | null>(null);

export const ShareProvider = ShareContext.Provider;

export function useShare(): ShareInfo | null {
  return useContext(ShareContext);
}

/** True when the share grants access to exactly one page (rules has a single
 * entry with includeChildren=false). Used by DocsLayout to hide the tree
 * sidebar — there's nothing else to navigate to. */
export function isSinglePageShare(share: ShareInfo | null): boolean {
  if (!share) return false;
  if (share.rules.length !== 1) return false;
  return !share.rules[0].includeChildren;
}
